import React, { useState, useEffect } from 'react';
import {
  Table,
  Typography,
  Button,
  Space,
  Input,
  Select,
  Modal,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import container from 'container/BrokerDashboard/editAgentContainer';

const { Title } = Typography;
const { Option } = Select;

// Function to extract agent ID from URL
const getAgentIdFromUrl = () => {
  const pathname = window.location.pathname;
  const match = pathname.match(/\/broker\/dashboard\/(\d+)\/orientation-info/);
  return match ? parseInt(match[1]) : null; // return integer if found, null if not found
};

const SuccessMessage = styled.div`
  border-radius: 8px;
  background: var(--Success-600, #16A34A);
  position: fixed;
  right: 79.5px;
  top: 84px;
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: white;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const StyledActions = styled(Space)`
  display: flex;
  gap: 16px;
  font-size: 16px;
`;
const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 5px;
`;
const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AddButton = styled(Button)`
  color: #8c8c8c !important;
  border-color: #d9d9d9 !important;
  border-radius: 8px !important;
  font-weight: 500;
  
  &:hover {
    color: #595959 !important;
    border-color: #bfbfbf !important;
    background: #fafafa !important;
  }
  
  &:focus {
    color: #8c8c8c !important;
    border-color: #d9d9d9 !important;
    background: #ffffff !important;
  }
`;

const StyledTable = styled(Table)`
  margin-right: 24px !important;
  
  .ant-table {
    border: none !important;
  }
  
  .ant-table-thead > tr > th {
    border: none !important;
    background: transparent !important;
  }
  
  .ant-table-tbody > tr > td {
    border: none !important;
  }
  
  .ant-table-thead > tr > th:first-child {
    width: 33.33% !important;
    text-align: left !important;
    padding-left: 16px !important;
  }
  
  .ant-table-tbody > tr > td:first-child {
    width: 33.33% !important;
    text-align: left !important;
    padding-left: 16px !important;
  }
  
  .ant-table-thead > tr > th:nth-child(2) {
    width: 33.33% !important;
    text-align: left !important;
  }
  
  .ant-table-tbody > tr > td:nth-child(2) {
    width: 33.33% !important;
    text-align: left !important;
  }
  
  .ant-table-thead > tr > th:last-child {
    width: 33.33% !important;
    text-align: center !important;
    padding-right: 32px !important;
  }
  
  .ant-table-tbody > tr > td:last-child {
    width: 33.33% !important;
    text-align: center !important;
    padding-right: 32px !important;
  }
`;

const AssociatesSection = ({ 
  getOnboardingAssociateInviteInfoResponse, 
  updateOnboardingInfo,
  getOnboardingAssociateInviteInfo  // Add this
}) => {
  const { data } = getOnboardingAssociateInviteInfoResponse || {};
  const rawData = data || [];
  const agentId = getAgentIdFromUrl();

  const [editedData, setEditedData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  // Create associates payload and send to API
  const createAndSendAssociatesPayload = (dataToSend = null, isNewAddition = false) => {
    let dataToUse;
    
    if (isNewAddition) {
      // Only send the newly added data
      dataToUse = dataToSend;
    } else {
      // Send all data (for updates/deletes)
      dataToUse = dataToSend || editedData;
    }
    
    const associatesPayload = {
      data: dataToUse.map(associate => ({
        firstname: associate.firstname || "",
        lastname: associate.lastname || "",
        phone: associate.phone || "",
        email: associate.email || "",
        member_type: associate.role?.toLowerCase().replace(/\s+/g, '_') || "associate"
      })),
      step_completed: "associate_invite",
      agent_id: agentId,
    };
    
    // Send payload to API with callback to refresh data
    if (updateOnboardingInfo && associatesPayload) {
      updateOnboardingInfo({ 
        payload: associatesPayload,
        callback: () => {
          // Refresh associate invite info data after update
          getOnboardingAssociateInviteInfo({ agentId: agentId, step: "associate_invite" });
          setShowSuccessMessage(true);
        }
      });
    }
    
    return associatesPayload;
  };

  useEffect(() => {
    const mappedData = rawData.map((item) => ({
      key: item.id,
      firstname: item.firstname || '',
      lastname: item.lastname || '',
      email: item.email || '',
      phone: item.phone || '',
      role: item.member_type?.replace(/_/g, ' ') || 'N/A',
      status: item.status || 'N/A',
    }));
    setEditedData(mappedData);
  }, [data]);

  // Remove this useEffect that causes the infinite loop
  // useEffect(() => {
  //   if (editedData.length > 0 && updateOnboardingInfo) {
  //     createAndSendAssociatesPayload();
  //   }
  // }, [editedData]);

  const handleDelete = () => {
    if (recordToDelete) {
      const updatedData = editedData.filter((item) => item.key !== recordToDelete.key);
      setEditedData(updatedData);
      
      // Call the API with the updated data
      createAndSendAssociatesPayload(updatedData);
      
      message.success('Associate deleted successfully!');
      setRecordToDelete(null);
      setDeleteConfirmVisible(false);
    }
  };

  const validateInputs = () => {
    let isValid = true;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!selectedRecord?.email || !emailRegex.test(selectedRecord.email)) {
      setEmailError('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError('');
    }

    return isValid;
  };

  // Add phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as XX-XXX-XXXX
    if (phoneNumber.length <= 2) {
      return phoneNumber;
    } else if (phoneNumber.length <= 5) {
      return `${phoneNumber.slice(0, 2)}-${phoneNumber.slice(2)}`;
    } else {
      return `${phoneNumber.slice(0, 2)}-${phoneNumber.slice(2, 5)}-${phoneNumber.slice(5, 9)}`;
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'firstname',
      key: 'name',
      width: '40%',
      align: 'left',
      render: (_, record) => (
        <div style={{ textAlign: 'left' }}>
          <div style={{
            color: '#111827',
            fontFamily: 'Inter',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '20px'
          }}>
            {`${record.firstname} ${record.lastname}`}
          </div>
          <div style={{
            color: '#4B5563',
            fontFamily: 'Inter',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '20px'
          }}>
            {record.email}
          </div>
        </div>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: '37%',
      align: 'left',
      render: (phone) => (
        <div style={{
          color: '#111827',
          fontFamily: 'Inter',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px'
        }}>
          {phone || '-'}
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: '23%',
      align: 'left',
      render: (role) => {
        // Format role text with proper capitalization
        const formatRole = (roleText) => {
          if (!roleText) return '-';
          
          // Handle special cases
          if (roleText.toLowerCase() === 'co-team lead') {
            return 'Co-team lead';
          }
          
          // General capitalization
          return roleText
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        return (
          <div style={{
            color: '#111827',
            fontFamily: 'Inter',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '20px'
          }}>
            {formatRole(role)}
          </div>
        );
      },
    },
    // {
    //   title: 'Status',
    //   dataIndex: 'status',
    //   key: 'status',
    // },
    // {
    //   title: 'Actions',
    //   key: 'actions',
    //   render: (_, record) => (
    //     <StyledActions>
    //       <EditOutlined
    //         onClick={() => {
    //           setSelectedRecord(record);
    //           setIsAdding(false);
    //           setEmailError('');
    //           setIsModalVisible(true);
    //         }}
    //       />
    //       <DeleteOutlined
    //         style={{ color: 'red' }}
    //         onClick={() => {
    //           setRecordToDelete(record);
    //           setDeleteConfirmVisible(true);
    //         }}
    //       />
    //     </StyledActions>
    //   ),
    // },
  ];

  return (
    <div>
      {showSuccessMessage && (
        <SuccessMessage>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Onboarding details has been updated
        </SuccessMessage>
      )}
      {rawData.length ? (
        <>
          <Header>
            <HeaderContent>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M6.75 9.75C6.75 9.33579 7.08579 9 7.5 9H16.5C16.9142 9 17.25 9.33579 17.25 9.75C17.25 10.1642 16.9142 10.5 16.5 10.5H7.5C7.08579 10.5 6.75 10.1642 6.75 9.75ZM7.5 13.5H16.5C16.9142 13.5 17.25 13.1642 17.25 12.75C17.25 12.3358 16.9142 12 16.5 12H7.5C7.08579 12 6.75 12.3358 6.75 12.75C6.75 13.1642 7.08579 13.5 7.5 13.5ZM21.75 5.25V19.5C21.7499 19.7599 21.6152 20.0012 21.394 20.1378C21.1729 20.2743 20.8968 20.2866 20.6644 20.1703L18 18.8381L15.3356 20.1703C15.1244 20.276 14.8756 20.276 14.6644 20.1703L12 18.8381L9.33562 20.1703C9.12436 20.276 8.87564 20.276 8.66438 20.1703L6 18.8381L3.33562 20.1703C3.1032 20.2866 2.82712 20.2743 2.60597 20.1378C2.38483 20.0012 2.25014 19.7599 2.25 19.5V5.25C2.25 4.42157 2.92157 3.75 3.75 3.75H20.25C21.0784 3.75 21.75 4.42157 21.75 5.25ZM20.25 5.25H3.75V18.2869L5.66437 17.3288C5.87564 17.223 6.12436 17.223 6.33563 17.3288L9 18.6619L11.6644 17.3288C11.8756 17.223 12.1244 17.223 12.3356 17.3288L15 18.6619L17.6644 17.3288C17.8756 17.223 18.1244 17.223 18.3356 17.3288L20.25 18.2869V5.25Z"
                  fill="#111827"
                />
              </svg>
              <SectionTitle>Associates</SectionTitle>
            </HeaderContent>
            <AddButton
              type="default"
              ghost
              onClick={() => {
                setIsAdding(true);
                setSelectedRecord({
                  key: Date.now(),
                  firstname: '',
                  lastname: '',
                  email: '',
                  phone: '',
                  role: '',
                  status: 'Pending',
                });
                setEmailError('');
                setIsModalVisible(true);
              }}
            >

              + Add another associate
            </AddButton>
          </Header>

          <StyledTable
            dataSource={editedData}
            columns={columns}
            pagination={false}
            bordered={false}
          />

          {/* Edit/Add Modal */}
          <Modal
            visible={isModalVisible}
            onCancel={() => {
              setSelectedRecord(null);
              setIsModalVisible(false);
              setIsAdding(false);
              setEmailError('');
            }}
            footer={null}
            width={480}
            centered
            closable
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                {isAdding ? 'Add associate' : 'Edit associate'}
              </div>
              <div style={{ fontSize: 14, color: '#888' }}>
                {isAdding
                  ? 'Enter associate information and role'
                  : 'Update associate information and role'}
              </div>

              {/* First Name */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 14 }}>First Name</label>
                <Input
                  value={selectedRecord?.firstname || ''}
                  onChange={(e) => {
                    setSelectedRecord((prev) => ({
                      ...prev,
                      firstname: e.target.value,
                    }));
                  }}
                />
              </div>

              {/* Last Name */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 14 }}>Last Name</label>
                <Input
                  value={selectedRecord?.lastname || ''}
                  onChange={(e) => {
                    setSelectedRecord((prev) => ({
                      ...prev,
                      lastname: e.target.value,
                    }));
                  }}
                />
              </div>

              {/* Email */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 14 }}>Email Address</label>
                <Input
                  value={selectedRecord?.email || ''}
                  onChange={(e) => {
                    setSelectedRecord((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }));
                    setEmailError('');
                  }}
                />
                {emailError && (
                  <div style={{ color: 'red', fontSize: 12 }}>{emailError}</div>
                )}
              </div>

              {/* Phone (optional) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 14 }}>Phone number (optional)</label>
                <Input
                  value={selectedRecord?.phone || ''}
                  onChange={(e) => {
                    const formattedPhone = formatPhoneNumber(e.target.value);
                    setSelectedRecord((prev) => ({
                      ...prev,
                      phone: formattedPhone,
                    }));
                  }}
                  placeholder="76-546-7123"
                  maxLength={12} // XX-XXX-XXXX = 12 characters
                />
              </div>

              {/* Role */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 14 }}>Role</label>
                <Select
                  value={selectedRecord?.role || ''}
                  onChange={(val) =>
                    setSelectedRecord((prev) => ({
                      ...prev,
                      role: val,
                    }))
                  }
                  placeholder="Select role"
                >
                  <Option value="Co Team lead">Co-Team lead</Option>
                  <Option value="Associate">Associate</Option>
                  <Option value="Assistant">Assistant</Option>
                  <Option value="Team TC">TC</Option>
                  <Option value="Admin Agent">Admin Agent</Option>
                  <Option value="Operation Agent">Operation Agent</Option>
                  <Option value="Referral Agent">Referral Agent</Option>
                </Select>
              </div>

              {/* Note */}
              <div
                style={{
                  backgroundColor: '#fff7e6',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #ffe58f',
                  fontSize: '14px',
                }}
              >
                ICAs will be manually sent by the Onboarding Ops Team once the
                details are verified.
              </div>

              {/* Footer Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button
                  onClick={() => {
                    setIsModalVisible(false);
                    setIsAdding(false);
                    setEmailError('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    if (!validateInputs()) return;

                    let updatedData;
                    if (isAdding) {
                      updatedData = [...editedData, selectedRecord];
                      setEditedData(updatedData);
                      // Send only the newly added data
                      createAndSendAssociatesPayload([selectedRecord], true);
                    } else {
                      updatedData = editedData.map((item) =>
                        item.key === selectedRecord.key ? selectedRecord : item
                      );
                      setEditedData(updatedData);
                      // Send all data for updates
                      createAndSendAssociatesPayload(updatedData, false);
                    }
                    
                    setIsModalVisible(false);
                    setIsAdding(false);
                    setEmailError('');
                  }}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            visible={deleteConfirmVisible}
            onCancel={() => {
              setRecordToDelete(null);
              setDeleteConfirmVisible(false);
            }}
            onOk={handleDelete}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            centered
          >
            <p>Are you sure you want to delete this associate?</p>
          </Modal>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
          No associates found. Add one to get started!
        </div>
      )}
    </div>
  );
};

export default container(AssociatesSection);
